import { watch, getCurrentInstance, onMounted, onBeforeUnmount, type Ref } from 'vue';
import type { FacetGroupModel, FacetItem } from '../DocumentSearch.types';
import { FACET_KEYS } from '../DocumentSearch.types';

type Options = {
  keyword: Ref<string>;
  filters: Ref<FacetGroupModel[]>;
  searchArchived: boolean;
  facetKeys?: readonly string[];
  onChangeFromPopstate?: () => void;
};

function getSelectedValues(group: FacetGroupModel): string[] {
  return (group?.data ?? [])
    .filter(i => i.checked)
    .map(i => i.value ?? i.name)
    .filter(Boolean) as string[];
}

export function useFacetUrlSync(opts: Options) {
  const {
    keyword,
    filters,
    searchArchived,
    onChangeFromPopstate,
  } = opts;

  const facetKeys = Array.isArray(opts.facetKeys) && opts.facetKeys.length
    ? opts.facetKeys
    : FACET_KEYS;

  let suppressNextFilterWatch = false;

  function serializeToURL({ push = false }: { push?: boolean } = {}) {
    const url = new URL(window.location.href);

    const q = keyword.value?.trim();
    if (q) url.searchParams.set('query', q);
    else url.searchParams.delete('query');

    for (const key of facetKeys) {
      const group = filters.value.find(g => g.id === key || g.name === key);
      const selected = group ? getSelectedValues(group) : [];
      if (selected.length) url.searchParams.set(key, selected.join(','));
      else url.searchParams.delete(key);
    }

    if (searchArchived) url.searchParams.set('archived', '1');
    else url.searchParams.delete('archived');

    const next = `${url.pathname}${url.search}${url.hash}`;
    const method: 'pushState' | 'replaceState' = push ? 'pushState' : 'replaceState';
    window.history[method]({}, '', next);
  }

  function parseFiltersFromURL(url: URL): FacetGroupModel[] {
    const groups: FacetGroupModel[] = [];

    for (const key of facetKeys) {
      const raw = url.searchParams.get(key);
      if (!raw) continue;

      const values = raw.split(',').map(s => s.trim()).filter(Boolean);
      if (!values.length) continue;

      const data: FacetItem[] = values.map(v => ({
        name: v,
        value: v,
        checked: true,
        count: 0,
        visible: true,
      }));

      groups.push({ id: key, name: key, data });
    }

    return groups;
  }

  function initFromUrl() {
    const url = new URL(window.location.href);
    keyword.value = url.searchParams.get('query') ?? '';

    const parsed = parseFiltersFromURL(url);
    suppressNextFilterWatch = true;
    filters.value = parsed;
  }

  function handlePopState() {
    initFromUrl();
    onChangeFromPopstate?.();
  }

  function registerPopstate() {
    window.addEventListener('popstate', handlePopState);
  }
  function unregisterPopstate() {
    window.removeEventListener('popstate', handlePopState);
  }

  if (getCurrentInstance()) {
    onMounted(registerPopstate);
    onBeforeUnmount(unregisterPopstate);
  }

  function watchFiltersEffect(effect: () => void, opts?: { flush?: 'pre' | 'post' | 'sync' }) {
	const flush = opts?.flush ?? 'sync';

    return watch(
      filters,
      () => {
        if (suppressNextFilterWatch) {
          suppressNextFilterWatch = false;
          return;
        }
        effect();
        serializeToURL({ push: false });
      },
      { deep: true, flush }
    );
  }

  return {
    serializeToURL,
    parseFiltersFromURL,
    initFromUrl,
    watchFiltersEffect,
    registerPopstate,
    unregisterPopstate,
  };
}
